import { React, useState, useEffect, useMemo, useContext } from "react";
import PropTypes from "prop-types";
import { useMsal } from "@azure/msal-react";
import { Box, Typography, CircularProgress } from "@mui/material";
import LoadBookingIcon from "../../../assets/icons/LoadBookingIcon";
import PrimarySelect from "../../Inputs/PrimarySelect/PrimarySelect";
import PrimaryTextField from "../../Inputs/PrimaryTextField/PrimaryTextField";
import PrimaryButton from "../../Buttons/PrimaryButton/PrimaryButton";
import CustomizedTable from "../../CustomizedTable/CustomizedTable";
import { DefaultCell } from "../../CustomizedTable/CustomCells/CustomCells";
import OfferStatusCell from "../../CustomizedTable/CustomCells/OfferStatusCell/OfferStatusCell";
import SelectedCell from "../../CustomizedTable/CustomCells/SelectedCell/SelectedCell";
import DateTimeRangeCell from "../../CustomizedTable/CustomCells/DateTimeRangeCell/DateTimeRangeCell";
import CurrencyCell from "../../CustomizedTable/CustomCells/CurrencyCell/CurrencyCell";
import PipelineStatusCell from "../../CustomizedTable/CustomCells/PipelineStatusCell/PipelineStatusCell";
import CarrierNameCell from "../../CustomizedTable/CustomCells/CarrierNameCell/CarrierNameCell";
import MilesCell from "../../CustomizedTable/CustomCells/MilesCell/MilesCell";
import DateRangeFilter from "../../CustomizedTable/Filters/DateRangeFilter/DateRangeFilter";
import SelectedFilter from "../../CustomizedTable/Filters/SelectedFilter/SelectedFilter";
import SelectColumnFilter from "../../CustomizedTable/Filters/SelectColumnFilter/SelectColumnFilter";
import OfferStatusFilter from "../../CustomizedTable/Filters/OfferStatusFilter/OfferStatusFilter";
import NumberRangeFilter from "../../CustomizedTable/Filters/NumberRangeFilter/NumberRangeFilter";
import SecurityCheck from "../../SecurityCheck/SecurityCheck";
import { sortArrayOfStringObjectsByValueAsc } from "../../../utils/sortHelpers";
import { getSumOfArray } from "../../../utils/calcHelpers";
import { CarrierService } from "../../../services/CarrierService";
import { AlertContext } from "../../Dashboard/Dashboard";

export default function OfferBoard({
  activeLoad,
  activeUser,
  isAdminUser,
  selectedOrderId,
  counter,
  setCounter,
  refreshOffers,
  setRefreshOffers,
  setRefreshPage,
  setRefreshLoads,
  setSearchParams,
  onLoadFieldUpdate,
  newlyAdded,
  updateNotifications,
  availableToBook,
  handleTableAction,
}) {
  const { instance } = useMsal();
  const { warning } = useContext(AlertContext);
  const [isLoading, setIsLoading] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [newLoadOffers, setNewLoadOffers] = useState([]);
  const [loadOffers, setLoadOffers] = useState([]);
  const [selectedOffers, setSelectedOffers] = useState([]);
  const [selectedOption, setSelectedOption] = useState(30);

  const options = useMemo(
    () => [
      {
        name: "Performance: 30 Days",
        value: 30,
      },
      {
        name: "Performance: 60 Days",
        value: 60,
      },
      {
        name: "Performance: 90 Days",
        value: 90,
      },
    ],
    []
  );

  //Reps
  const [amount, setAmount] = useState(null);

  const newOffers = useMemo(() => newLoadOffers, [newLoadOffers]);
  const offers = useMemo(() => loadOffers, [loadOffers]);

  const columns = useMemo(
    () => [
      {
        Header: "",
        accessor: "selected",
        Filter: SelectedFilter,
        width: 50,
        Cell: SelectedCell,
        sortDisabled: true,
        showFilter: true,
      },
      {
        Header: "Last Updated",
        accessor: "lastUpdatedDate",
        Filter: DateRangeFilter,
        filter: "dateBetween",
        width: 130,
        Cell: DateTimeRangeCell,
      },
      {
        Header: "Status",
        accessor: "approvedStatus",
        Filter: OfferStatusFilter,
        filter: "multi",
        width: 100,
        Cell: OfferStatusCell,
      },
      {
        Header: "Offer",
        accessor: "amount",
        Filter: NumberRangeFilter,
        filter: "between",
        width: 90,
        Cell: CurrencyCell,
      },
      {
        Header: "Counter",
        accessor: "counterOffer",
        Filter: NumberRangeFilter,
        filter: "between",
        width: 90,
        Cell: CurrencyCell,
      },
      {
        Header: "Carrier",
        accessor: "carrierName",
        idKey: "carrierId",
        originPoint: "offers-dashboard",
        showCanBook: true,
        showMetrics: true,
        filter: "fuzzyText",
        width: 240,
        Cell: CarrierNameCell,
      },
      {
        Header: "MC",
        accessor: "icc_number",
        filter: "fuzzyText",
        width: 80,
        Cell: DefaultCell,
      },
      {
        Header: "Miles to Origin",
        accessor: "miles",
        Filter: NumberRangeFilter,
        filter: "between",
        width: 140,
        Cell: MilesCell,
      },
      {
        Header: "Location",
        accessor: "cityState",
        filter: "fuzzyText",
        width: 200,
        Cell: DefaultCell,
      },
      {
        Header: "Empty",
        accessor: "expEmptyTime",
        Filter: DateRangeFilter,
        filter: "dateBetween",
        width: 130,
        Cell: DateTimeRangeCell,
      },
      {
        Header: "Note",
        accessor: "comments",
        filter: "fuzzyText",
        width: 600,
        Cell: DefaultCell,
      },
      {
        Header: "Carrier Rep",
        accessor: "enteredUserId",
        filter: "fuzzyText",
        width: 120,
        Cell: DefaultCell,
      },
      {
        Header: "Carrier Status",
        accessor: "active",
        Filter: SelectColumnFilter,
        filter: "equal",
        width: 140,
        Cell: DefaultCell,
      },
      {
        Header: "Pipeline Status",
        accessor: "pipelineStatus",
        Filter: SelectColumnFilter,
        filter: "equal",
        width: 140,
        Cell: PipelineStatusCell,
      },
    ],
    []
  );

  //Admins
  const handleCounter = () => {
    if (counter) {
      selectedOffers.forEach((offer) => {
        const payload = {
          userId: activeUser.userIdMcld,
          isManager: isAdminUser,
          carrierOfferId: offer.id,
          status: "countered",
          counterOffer: counter,
        };

        const maxPayPayload = {
          orderId: activeLoad.id,
          movementId: activeLoad.movementId,
          overrideMaxPay: counter,
        };

        setIsLoading(true);
        setSubmitDisabled(true);

        CarrierService.UpdateMaxPay(instance, maxPayPayload)
          .then(() => CarrierService.UpdateOfferAction(instance, payload))
          .then(() => {
            onLoadFieldUpdate?.(activeLoad.id, "maxPay", counter);
            if (selectedOffers.length === loadOffers.length) {
              setSearchParams("");
              setRefreshLoads(true);
            }
            setCounter(null);
            setRefreshPage(true);
          })
          .catch((error) => {
            console.error(error);
            warning(error);
          })
          .finally(() => {
            setIsLoading(false);
            setSubmitDisabled(false);
          });
      });
    }
  };

  const handleAccept = () => {
    setIsLoading(true);
    setSubmitDisabled(true);
    selectedOffers.forEach((offer) => {
      const payload = {
        userId: activeUser.userIdMcld,
        isManager: isAdminUser,
        carrierOfferId: offer.id,
        status: "approved",
      };

      CarrierService.UpdateOfferAction(instance, payload)
        .then(() => {
          setIsLoading(false);
          setSubmitDisabled(false);
          setRefreshLoads(true);
          setRefreshPage(true);
        })
        .catch((error) => {
          console.error(error);
          setIsLoading(false);
          setSubmitDisabled(false);
          warning(error);
        });
    });
  };

  //Reps
  const handleUpdate = () => {
    if (amount) {
      let submitted = 0;
      selectedOffers.forEach((offer) => {
        offer.approvedStatus = null;
        offer.amount = amount;

        setIsLoading(true);
        setSubmitDisabled(true);
        CarrierService.UpdateCarrierOffer(instance, offer)
          .then(() => {
            submitted++;
            setIsLoading(false);
            setSubmitDisabled(false);
            if (submitted === selectedOffers.length) {
              setAmount(null);
              setRefreshLoads(true);
            }
          })
          .catch((error) => {
            console.error(error);
            setIsLoading(false);
            setSubmitDisabled(false);
            warning(error);
          });
      });
    }
  };

  const scorecardByCarrierId = (scorecards) =>
    Object.fromEntries(scorecards.map((s) => [s.carrierId, s]));

  const getLoadOffers = () => {
    if (selectedOrderId) {
      setIsLoading(true);
      CarrierService.GetLoadOffers(instance, selectedOrderId, selectedOption)
        .then((res) => {
          const orderedOffers = sortArrayOfStringObjectsByValueAsc(
            res.offers.filter(({ amount }) => amount != null && amount > 0),
            "enteredUserId",
            activeUser.userIdMcld
          );

          const scorecards = scorecardByCarrierId(res.carrierScorecard);

          orderedOffers.forEach((offer) => {
            offer.selected = false;
            offer.active = offer.carrierStatus === "A" ? "Active" : "Inactive";

            const carrierScorecard = scorecards[offer.carrierId];
            
            if (carrierScorecard) {
              offer.orders = carrierScorecard.orderCount;
              offer.otp = `${Math.round(carrierScorecard.onTimePickup)}%`;
              offer.otd = `${Math.round(carrierScorecard.onTimeDelivery)}%`;
              offer.bounceCount = carrierScorecard.bounceCount;
              offer.bounce = `${Math.round(carrierScorecard.bounce)}%`;
              offer.sameDayBounce = `${Math.round(
                carrierScorecard.sameDayBounce
              )}%`;
              offer.techTracking = `${Math.round(
                carrierScorecard.techTracking
              )}%`;
            }
          });

          const newOffers = isAdminUser
            ? orderedOffers.filter(
              ({ approvedStatus }) =>
                approvedStatus === "null" || approvedStatus === "4"
            )
            : orderedOffers.filter(
              ({ enteredUserId }) => enteredUserId === activeUser.userIdMcld
            );
          const offers = isAdminUser
            ? orderedOffers.filter(
              ({ approvedStatus }) =>
                approvedStatus != "null" && approvedStatus != "4"
            )
            : orderedOffers.filter(
              ({ enteredUserId }) => enteredUserId != activeUser.userIdMcld
            );

          if (isAdminUser && newOffers.length === 0 && newlyAdded)
            updateNotifications();

          setNewLoadOffers(newOffers);
          setLoadOffers(offers);
          setIsLoading(false);
        })
        .catch((error) => {
          console.error(error);
          setIsLoading(false);
          warning(error);
        });
    }
  };

  const updateDataNew = (rowIndex, columnID, value) => {
    setNewLoadOffers((old) =>
      old.map((row, index) => {
        if (index === rowIndex) {
          return {
            ...row,
            [columnID]: value,
          };
        }
        return row;
      })
    );
  };

  const updateData = (rowIndex, columnID, value) => {
    setLoadOffers((old) =>
      old.map((row, index) => {
        if (index === rowIndex) {
          return {
            ...row,
            [columnID]: value,
          };
        }
        return row;
      })
    );
  };

  const handleSelectAllNew = (value) => {
    let updatedLoadOffers = [...newLoadOffers];
    updatedLoadOffers.forEach((offer) => {
      offer.selected = value;
    });
    setNewLoadOffers(updatedLoadOffers);
  };

  const handleSelectAll = (value) => {
    let updatedLoadOffers = [...loadOffers];
    updatedLoadOffers.forEach((offer) => {
      offer.selected = value;
    });
    setLoadOffers(updatedLoadOffers);
  };

  useEffect(() => {
    if (selectedOffers.length > 0) {
      setSubmitDisabled(false);
    } else {
      setSubmitDisabled(true);
    }
  }, [selectedOffers]);

  useEffect(() => {
    let selectedLoadOffers = newLoadOffers.filter(
      ({ selected }) => selected === true
    );
    selectedLoadOffers = selectedLoadOffers.concat(
      loadOffers.filter(({ selected }) => selected === true)
    );
    setSelectedOffers(selectedLoadOffers);
  }, [loadOffers, newLoadOffers]);

  useEffect(() => {
    getLoadOffers();
  }, [selectedOption]);

  useEffect(() => {
    if (refreshOffers) {
      setRefreshOffers(false);
      getLoadOffers();
    }
  }, [refreshOffers]);

  return (
    <Box sx={{ margin: "12px 24px" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h3" color="black_opacity.100">
          Offers
        </Typography>
        <Box sx={{ display: "flex" }}>
          <Box>
            <PrimarySelect
              options={options}
              onChange={setSelectedOption}
              value={selectedOption}
              nameKey="name"
              valueKey="value"
            />
          </Box>
          <SecurityCheck
            componentName="CarrierOffersDashboardManager"
            action="Read"
          >
            <Box marginLeft="10px">
              <PrimaryTextField
                id="load-offers-header-manager-counter"
                placeholder="Counter"
                onEnter={handleCounter}
                onChange={(v) => {
                  const value = parseFloat(v) || 0;
                  setCounter(value);
                }}
                value={counter}
                width="150px"
                isNumber
                isCurrency
                endAdormentButton
                disabled={submitDisabled}
              />
            </Box>
            <Box marginLeft="10px">
              <PrimaryButton
                label="Reject Offer"
                onClick={() =>
                  handleTableAction({
                    action: "reject-offer",
                    offersRejected: selectedOffers,
                    previousOffers: loadOffers,
                    newOffersCount: newLoadOffers.length,
                    isManager: isAdminUser,
                  })
                }
                disabled={submitDisabled}
              />
            </Box>
            <Box marginLeft="10px">
              <PrimaryButton
                label="Accept Offer"
                onClick={handleAccept}
                disabled={submitDisabled}
              />
            </Box>
          </SecurityCheck>
          <SecurityCheck
            componentName="CarrierOffersDashboardRep"
            action="Read"
          >
            <Box marginLeft="10px">
              <PrimaryTextField
                id="load-offers-header-rep-update-offer"
                placeholder="Update offer"
                onEnter={handleUpdate}
                onChange={setAmount}
                value={amount}
                isNumber
                isCurrency
                endAdormentButton
                disabled={submitDisabled}
              />
            </Box>
            {availableToBook && (
              <Box marginLeft="10px">
                <PrimaryButton
                  label="Book"
                  width="70px"
                  onClick={() =>
                    handleTableAction({
                      action: "load-booking",
                      loadId: selectedOrderId,
                      carrierId: selectedOffers[0].carrierId,
                      offerAmount: selectedOffers[0].amount,
                      carrierOfferId: selectedOffers[0].id,
                    })
                  }
                  disabled={submitDisabled || selectedOffers.length > 1}
                  startIcon={LoadBookingIcon}
                />
              </Box>
            )}
          </SecurityCheck>
        </Box>
      </Box>
      {isLoading ? (
        <Box
          sx={{ display: "flex", justifyContent: "center", marginTop: "20px" }}
        >
          <CircularProgress sx={{ color: "primary.60" }} size={40} />
        </Box>
      ) : (
        <Box sx={{ height: "calc(100vh - 360px)", overflow: "scroll" }}>
          <Box>
            <Typography variant="h4">
              {isAdminUser ? "New" : "Your Offers"}
            </Typography>
            <CustomizedTable
              id="new-load-offers-dashboard"
              columns={columns}
              data={newOffers}
              updateData={updateDataNew}
              setSelectAll={handleSelectAllNew}
              defaultHeight={newOffers.length * 32}
              defaultWidth={getSumOfArray(columns.map(({ width }) => width))}
              actionCallback={handleTableAction}
            />
          </Box>
          <Box>
            <Typography variant="h4">
              {isAdminUser ? "Previous" : "Other offers"}
            </Typography>
            <CustomizedTable
              id="load-offers-dashboard"
              columns={columns}
              data={offers}
              updateData={updateData}
              setSelectAll={handleSelectAll}
              defaultHeight={offers.length * 32}
              defaultWidth={getSumOfArray(columns.map(({ width }) => width))}
              actionCallback={handleTableAction}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}

OfferBoard.propTypes = {
  activeLoad: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    movementId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      .isRequired,
  }).isRequired,
  activeUser: PropTypes.object,
  isAdminUser: PropTypes.bool,
  selectedOrderId: PropTypes.string,
  counter: PropTypes.number,
  setCounter: PropTypes.func.isRequired,
  refreshOffers: PropTypes.bool,
  setRefreshOffers: PropTypes.func,
  setRefreshPage: PropTypes.func,
  setRefreshLoads: PropTypes.func,
  setSearchParams: PropTypes.func,
  onLoadFieldUpdate: PropTypes.func,
  newlyAdded: PropTypes.bool,
  updateNotifications: PropTypes.func,
  availableToBook: PropTypes.bool,
  handleTableAction: PropTypes.func,
};
